import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, ArrowLeft, PhoneCall } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

export default function Privacy() {
  const location = useLocation();
  const isPublic = !location.pathname.startsWith('/app');

  const cardClass = isPublic ? "bg-slate-800/50 border-slate-700" : "";

  return (
    <div className={isPublic ? "min-h-screen bg-slate-950 text-slate-100" : ""}>
      {/* Public header */}
      {isPublic && (
        <div className="bg-slate-950 border-b border-slate-800">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
                <PhoneCall className="w-4 h-4 text-white" />
              </div>
              <span className="text-lg font-semibold text-white">CONNECT</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="text-slate-300 hover:text-white hover:bg-slate-800">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      )}

      <div className="p-6 max-w-4xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${isPublic ? "bg-cyan-500/20" : "bg-primary/10"}`}>
              <Shield className={`w-6 h-6 ${isPublic ? "text-cyan-400" : "text-primary"}`} />
            </div>
            <div>
              <h1 className="text-3xl font-bold">Privacy Policy</h1>
              <p className={isPublic ? "text-slate-400" : "text-muted-foreground"}>Last updated: January 2025</p>
            </div>
          </div>
        </div>

        <div className="space-y-6">
          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>1. Introduction</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                CONNECT ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy 
                explains how we collect, use, disclose, and safeguard your information when you use our 
                Cloud PBX Operator Console service.
              </p>
              <p>
                Please read this privacy policy carefully. If you do not agree with the terms of this 
                privacy policy, please do not access the application.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>2. Information We Collect</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <h4>Personal Information</h4>
              <p>We may collect personal information that you voluntarily provide when using our service, including:</p>
              <ul>
                <li>Name and contact information</li>
                <li>Phone numbers and SIP credentials</li>
                <li>Profile pictures</li>
                <li>Company and organization details</li>
              </ul>

              <h4>Call Data</h4>
              <p>As part of providing our telecommunications service, we collect:</p>
              <ul>
                <li>Call logs (caller ID, called number, duration, timestamps)</li>
                <li>Call recordings (if enabled by your organization)</li>
                <li>Voicemail messages</li>
              </ul>

              <h4>Technical Data</h4>
              <p>We automatically collect certain information when you use the service:</p>
              <ul>
                <li>Device and browser information</li>
                <li>IP addresses</li>
                <li>Usage patterns and preferences</li>
              </ul>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>3. How We Use Your Information</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>We use the collected information for various purposes:</p>
              <ul>
                <li>To provide and maintain our service</li>
                <li>To process and complete calls</li>
                <li>To manage your account and provide customer support</li>
                <li>To send administrative information and service updates</li>
                <li>To improve our service and develop new features</li>
                <li>To comply with legal obligations</li>
              </ul>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>4. Information Sharing</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>We may share your information in the following situations:</p>
              <ul>
                <li><strong>With your organization:</strong> Your employer or organization administrator may have access to call data and usage information.</li>
                <li><strong>Service providers:</strong> We may share information with third-party vendors who assist in providing our service.</li>
                <li><strong>Legal requirements:</strong> We may disclose information if required by law or to protect our rights.</li>
                <li><strong>Business transfers:</strong> In connection with any merger, acquisition, or sale of assets.</li>
              </ul>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>5. Data Security</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We implement appropriate technical and organizational security measures to protect your 
                personal information. However, no method of transmission over the Internet or electronic 
                storage is 100% secure, and we cannot guarantee absolute security.
              </p>
              <p>Security measures include:</p>
              <ul>
                <li>Encryption of data in transit (TLS/SRTP)</li>
                <li>Secure authentication mechanisms</li>
                <li>Access controls and audit logging</li>
                <li>Regular security assessments</li>
              </ul>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>6. Data Retention</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We retain your personal information for as long as necessary to provide our services 
                and fulfill the purposes outlined in this policy. Call records are retained according 
                to your organization's retention policies and applicable legal requirements.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>7. Your Rights</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>Depending on your location, you may have the following rights:</p>
              <ul>
                <li>Access to your personal information</li>
                <li>Correction of inaccurate data</li>
                <li>Deletion of your data</li>
                <li>Data portability</li>
                <li>Withdrawal of consent</li>
              </ul>
              <p>
                To exercise these rights, please contact your organization administrator or our 
                support team.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>8. Cookies and Tracking</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We use local storage and session cookies to maintain your login session and 
                preferences. These are essential for the service to function properly.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>9. Changes to This Policy</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                We may update this privacy policy from time to time. We will notify you of any 
                changes by posting the new policy on this page and updating the "Last updated" date.
              </p>
            </CardContent>
          </Card>

          <Card className={cardClass}>
            <CardHeader>
              <CardTitle>10. Contact Us</CardTitle>
            </CardHeader>
            <CardContent className="prose prose-sm dark:prose-invert max-w-none">
              <p>
                If you have questions about this Privacy Policy or our data practices, please 
                contact us through the information provided on our{' '}
                <Link to={isPublic ? "/" : "/app/about"} className="text-primary hover:underline">
                  {isPublic ? "website" : "About page"}
                </Link>.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Footer for public page */}
        {isPublic && (
          <div className="mt-12 pt-6 border-t border-slate-800 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} Connect-US. All rights reserved.</p>
            <div className="flex justify-center gap-4 mt-2">
              <Link to="/terms" className="hover:text-slate-300">Terms & Conditions</Link>
              <span>•</span>
              <Link to="/" className="hover:text-slate-300">Home</Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
